import { register, SwiperContainer } from 'swiper/element';
import { A11y, Autoplay, Navigation, Pagination } from 'swiper/modules';
import paginationStyles from 'swiper/element/css/pagination';
import { AutoplayOptions, SwiperOptions } from 'swiper/types';
import { getNavigationA11yParams, stripInvalidAriaControls } from './SwiperA11y';

export class SliderInitializer {
	/**
	 * Initializes the Slider.
	 *
	 * @returns void
	 */
	init() {
		register();

		// Get all swipers and initialize them with the given parameters
		const swiperElements = document.querySelectorAll<SwiperContainer>(
			'.slider-module-container.primary swiper-container'
		);
		if (!swiperElements.length) return;

		swiperElements.forEach((swiper: SwiperContainer) => {
			const moduleId = swiper.closest('.slider-module-container.primary')!.id;

			const swiperButtonPrev = swiper.parentElement!.querySelector<HTMLElement>('.swiper-button-prev');
			const swiperButtonNext = swiper.parentElement!.querySelector<HTMLElement>('.swiper-button-next');
			const navigationContainer = swiper.parentElement!.querySelector<HTMLElement>('.slider-navigation');

			// Get parameters transmitted by backend settings
			const slidesPerView = +(swiper.getAttribute('data-slidesPerView') || 1);
			const loop = swiper.getAttribute('data-loop') === 'true';
			const autoplay = swiper.getAttribute('data-autoplay');
			const autoplayInterval = +(swiper.getAttribute('data-autoplayInterval') || 3000);
			let spaceBetweenData = swiper.getAttribute('data-spaceBetween');
			let spaceBetween;
			let disablePagination = !(swiper.getAttribute('data-pagination') == 'true');
			let enableNavigation = !!(swiper.getAttribute('data-navigation') == '1');

			switch (spaceBetweenData) {
				case 'no-spacing':
					spaceBetween = 0;
					break;
				case 'slider-spacing-xxl':
					spaceBetween = 132;
					break;
				case 'slider-spacing-xl':
					spaceBetween = 100;
					break;
				case 'slider-spacing-l':
					spaceBetween = 64;
					break;
				case 'slider-spacing-m':
					spaceBetween = 45;
					break;
				case 'slider-spacing-s':
					spaceBetween = 35;
					break;
				case 'slider-spacing-xs':
					spaceBetween = 25;
					break;
				case 'slider-spacing-xxs':
					spaceBetween = 15;
					break;

				default:
					spaceBetween = 0;
					break;
			}

			let autoplayParams: AutoplayOptions | boolean = false;
			if (autoplay != 'false') {
				autoplayParams = {
					delay: autoplayInterval,
					disableOnInteraction: false,
					pauseOnMouseEnter: true,
					stopOnLastSlide: true,
				};
			}

			// Core element instead of the all-modules bundle: only the modules actually used are shipped
			const swiperModules: any[] = [A11y, Autoplay];
			const swiperConfig: SwiperOptions = {};

			if (enableNavigation == true) {
				swiperModules.push(Navigation);
				swiperConfig.navigation = {
					prevEl: swiperButtonPrev,
					nextEl: swiperButtonNext,
				};
			}

			if (disablePagination != true) {
				swiperModules.push(Pagination);
				swiperConfig.pagination = {
					clickable: true,
					dynamicBullets: true,
					dynamicMainBullets: 3,
				};
			} else {
				swiper.classList.add('no-pagination');
			}

			const responsiveSlidesPerView = this.getResponsiveSlides(slidesPerView);

			const swiperParams: SwiperOptions = {
				modules: swiperModules,
				...swiperConfig,
				slidesPerView: responsiveSlidesPerView.mobile,
				slidesPerGroup: 1,
				loop: loop,
				autoplay: autoplayParams,
				spaceBetween: spaceBetween,
				preventInteractionOnTransition: true,
				a11y: getNavigationA11yParams(navigationContainer),

				breakpoints: {
					980: {
						slidesPerView: responsiveSlidesPerView.desktop,
					},
					767: {
						slidesPerView: responsiveSlidesPerView.tablet,
					},
				},

				injectStyles: [
					paginationStyles,
					`
					:host .swiper-pagination-bullets-dynamic :is(.swiper-pagination-bullet-active-next, .swiper-pagination-bullet-active-prev) {
						transform: scale(1);
						width: 24px;
						background-color: var(--color-gray-20);
					}

					:host .swiper-pagination-bullets-dynamic :is(.swiper-pagination-bullet-active-next-next, .swiper-pagination-bullet-active-prev-prev) {
						transform: scale(1);
						width: 16px;
						background-color: var(--color-gray-10);
					}
				`,
				],
			};

			Object.assign(swiper, swiperParams);
			swiper.initialize();
			stripInvalidAriaControls(swiperButtonPrev, swiperButtonNext);
		});
	}

	getResponsiveSlides(slidesPerView: number): ResponsiveSlidesPerView {
		const tablet = slidesPerView > 4 ? 4 : slidesPerView > 2 ? 2 : 1;
		const mobile = slidesPerView > 2 ? 2 : 1;

		return new ResponsiveSlidesPerView(slidesPerView, tablet, mobile);
	}
}

class ResponsiveSlidesPerView {
	desktop: number;
	tablet: number;
	mobile: number;

	constructor(desktop: number, tablet: number, mobile: number) {
		this.desktop = desktop;
		this.tablet = tablet;
		this.mobile = mobile;
	}
}
